/*!
 * rr-finder.js: behaviour layer for the GoVedia vehicle finder
 * (Raptor Racing brand spec section 6, "Vehicle selector", owner-approved
 * v2.10, reference vehicle-selector-options.html; 25 Sept 2026).
 *
 * Loaded from layout/theme.liquid with defer:
 *   <script src="{{ 'rr-finder.js' | asset_url }}" defer></script>
 * Markup: snippets/auto-part-search.liquid. Styling: assets/govedia-ov.css.
 * ES5, no dependencies (jQuery is on the page for GoVedia, it is not used
 * here). GoVedia (car.govedia.com) keeps owning the selects and its two
 * <input type="submit"> buttons, with their values, their disabled state and
 * their jQuery click handlers: this file never removes, moves, re-creates or
 * disables any of them. Two PROXY buttons stand in for the hidden inputs and
 * forward clicks to them. The whole file runs inside try/catch and every
 * query is guarded, so a GoVedia change can never break the page.
 *
 * Hooks, in the order they run:
 *  0. BOOT on DOMContentLoaded (at once if the document is already parsed).
 *     Find #gv-mmy and the .gv-mmy-bar around it; stop if either is missing.
 *     Pre-render the saved line from the URL tag (hook 6) before GoVedia
 *     loads, so a results page never flashes the fields first.
 *  1. BIND DETECTION: poll every 250 ms for up to 30 s until #gv-mmy holds
 *     input.gv-btn-search and a select.mk with more than one option (GoVedia
 *     has bound), and observe #gv-mmy (MutationObserver, childList only, not
 *     subtree) so the bind step re-runs whenever GoVedia empties and rebuilds
 *     it. Each poll tick and each observer callback runs the bind step,
 *     refresh() and the saved check. The poll stops once GoVedia has bound
 *     and the saved decision is settled.
 *  2. BIND STEP (idempotent: guarded by data-rr-bound on .mmy-ops and a
 *     lookup for an existing .gv-go): append two proxy buttons to
 *     .mmy-tab.mmy-ops after GoVedia's inputs, built with createElement:
 *     button.gv-go.rr-button--primary (label from the hidden Search input's
 *     value when it is set and is not "Loading...", else "Search"; the
 *     rr-button--primary class gives it the brand sheet's 5.2 claw swipe) and
 *     button.gv-clr.gv-clear (glyph span + "Clear" span, the same structure
 *     as the two saved-line buttons in the liquid). The step also runs on the
 *     liquid placeholder before GoVedia binds, so the green Search button is
 *     there from the first paint; GoVedia discards that copy together with
 *     the placeholder and the observer puts a fresh pair in the rebuilt
 *     .mmy-ops. The inputs' values are never changed. govedia-ov.css hides
 *     the two inputs only while .mmy-ops carries data-rr-bound (set right
 *     after the append), so if this file never runs, GoVedia's own Search
 *     and Clear stay visible and working.
 *  3. STEP STATE, refresh(): the tabs are .mmy-make, .mmy-model and
 *     .mmy-year (fallback: the closest .mmy-tab of select.mk, .md or .yr).
 *     Each gets .is-done while its select has a value, loses .is-miss once it
 *     has one, and .is-locked while the step before it is empty (model while
 *     make is empty, year while model is empty; a visual cue only, GoVedia
 *     owns disabled). The bar gets .has-pick while any select has a value
 *     (the Clear link shows) and .is-complete while all three do. Runs after
 *     bind, on every change event that bubbles out of #gv-mmy (listened for
 *     on the bar; that also marks the bar data-rr-touched) and on each poll
 *     tick.
 *  4. SEARCH PROXY click: with a step still empty, the first empty tab in
 *     make, model, year order gets .is-miss (the others lose it) and its
 *     select is focused; nothing is submitted. With all three filled, the
 *     hidden input.gv-btn-search loses its disabled attribute if GoVedia has
 *     not caught up yet, the proxy reads "Loading" with aria-busy="true", and
 *     the input gets a native click() so GoVedia's own jQuery handler builds
 *     the tag, writes its cookies and navigates. If the page is still here
 *     after 8 s the label and aria-busy are restored.
 *  5. CLEAR PROXY click (and .gv-saved__clear): the saved line is hidden at
 *     once so the fields show while GoVedia works. If input.gv-btn-clear
 *     exists and is enabled it gets a native click() (GoVedia erases its
 *     cookies, then navigates or resets in place; refresh() runs right after,
 *     because the in-place reset fires no change event). Otherwise the reset
 *     is manual: select.mk is set to "" with a bubbling change event
 *     (GoVedia's delegated handler then empties the model and year lists),
 *     .md and .yr get the same if they still hold a value, every .is-miss
 *     goes, refresh() runs and select.mk is focused. One addition: when the
 *     saved line was pre-rendered from the URL tag and GoVedia never filled
 *     the selects (a visitor with no gv cookie opening a results link), Clear
 *     navigates to the collection without the vehicle segment, which is what
 *     GoVedia's own Clear does on that page.
 *  6. SAVED STATE, only on a vehicle results page: window.MMY_Collection ===
 *     true with a non-empty MMY_Tags that names the selects' vehicle, or that
 *     vehicle's tag in the last segment of location.pathname. GoVedia
 *     pre-fills the selects from its gv cookie on every page, so on the
 *     homepage a remembered vehicle must show the pre-filled fields, the
 *     green Search and the Clear link, never "Showing parts for". The tag is
 *     the one GoVedia's RedirectToCurrentTags builds: the make, model and
 *     year option values joined with "-" (Make-Model-Year, for example
 *     Acura-ADX-2026; a submodel goes before the year in stores that use one,
 *     the year is left out when it is 9999, and EncodeTag then turns spaces,
 *     "&", "." and "," into "-" and may add a "ymm_" prefix), so both sides
 *     are normalized the same way here before comparing. The tag has to name
 *     the selects' vehicle in full: the three parts as one run, or, for a
 *     store with a submodel step, "Make-Model-" somewhere in it with "-Year"
 *     as its last segment. The make and the year alone never count, so a gv
 *     cookie that remembers another vehicle (ILX) than the URL filters by
 *     (ADX) can never rewrite the line. After bind, and on
 *     each poll tick, if all three selects have values and the bar does not
 *     carry data-rr-touched (set when the user changes any select or clicks a
 *     proxy), "YEAR MAKE MODEL" is built from the selected options' text
 *     (year first, as the reference's s[2] s[0] s[1]), written into
 *     .gv-saved__vehicle with textContent, the hidden attribute is dropped
 *     from .gv-saved and .is-saved goes on the bar (govedia-ov.css keys the
 *     display on the class, with [hidden] listed last, so the attribute and
 *     the class always move together); then the poll stops. At boot, if
 *     MMY_Collection is true and MMY_Tags ends in a four-digit year, the line
 *     is pre-rendered from the tag: from the gv_vehicle_json cookie's make,
 *     model and year when that cookie names the same vehicle, else the tag's
 *     first segment as the make and the middle as the model. The text is
 *     corrected from the selects once bound; when the selects hold another
 *     vehicle or none, the pre-rendered line stays, because the URL still
 *     filters by that vehicle. .gv-saved__change: removes .is-saved, puts
 *     hidden back on .gv-saved, marks the bar data-rr-touched, focuses
 *     select.mk, and on phones (max-width 798px) adds .gv-expanded to the bar
 *     so the fields open.
 *  6b. EVERY PAGE (28 Sept 2026, owner: "Showing parts for x car disappears
 *     after moving to another page"): once GoVedia has filled all three
 *     selects from its cookie and the visitor has not touched them, the saved
 *     line shows on every page, not only on results pages. On a results page
 *     it reads "Showing parts for"; elsewhere "Your vehicle:" with a Shop parts
 *     button (a proxy for GoVedia's Search, so it lands on the vehicle's
 *     results). A pre-rendered line from the URL tag is never overwritten.
 *  9. GARAGE (28 Sept 2026): Save to My Garage on the saved line and the
 *     compact bar. GoVedia's script keeps its MMY object private, but it draws
 *     "+ Add Current Vehicle" (a.add-vehicle, which saves the vehicle in the
 *     bar's selects) into #gv-save-vehicles, the hidden mount in the snippet,
 *     the same link govedia.mygarage.js clicks on the My Garage page. Signed
 *     in: Save clicks that link and GoVedia's own status text is mirrored into
 *     .gv-status. Guests: a sign-in prompt; the vehicle is remembered in
 *     localStorage for 30 minutes and saved on the first page view after
 *     sign-in where the selects still hold it. A vehicle already in the
 *     garage shows "In My Garage", which links to the My Garage page.
 * 10. COMPACT BAR (28 Sept 2026, owner: "when scrolling down it completely
 *     disappears"): .rr-vmini, built here and appended to <body>, pins under
 *     the sticky header (desktop section or #mobile-header, whichever is on
 *     screen) once the whole selector has scrolled under it. Empty: "Shop by
 *     vehicle" and Select vehicle, which scrolls back to the selector and
 *     focuses Make (opening the phone bar). Saved: the vehicle, Shop parts
 *     (off results pages, desktop), Save to My Garage and Change.
 *  7. MOBILE: a MutationObserver on the bar's class mirrors .gv-expanded
 *     (the theme's govedia.js toggles it on tap, Enter and Space) to
 *     aria-expanded on .gv-prompt-mobile. No click toggle of its own.
 *  8. KEYBOARD: the proxies are real buttons, so nothing extra; .gv-clear is
 *     display:none until .has-pick, so it is not focusable while hidden. On
 *     phones govedia-ov.css keeps the collapsed .gv-inner visibility:hidden
 *     until the bar has .gv-expanded, so the selects and Search leave the tab
 *     order while the bar is closed (visible again at once when it opens, so
 *     the focus in .gv-saved__change lands).
 */
(function () {
  'use strict';
  try {
    if (typeof document === 'undefined' || typeof window === 'undefined') { return; }

    var win = window;
    var doc = document;
    var POLL_MS = 250;
    var POLL_LIMIT_MS = 30000;
    var BUSY_MS = 8000;
    var PHONE_MQ = '(max-width: 798px)';
    var VEHICLE_COOKIE = 'gv_vehicle_json';
    var STEPS = [
      { cls: 'mk', tab: 'mmy-make', label: 'Make' },
      { cls: 'md', tab: 'mmy-model', label: 'Model' },
      { cls: 'yr', tab: 'mmy-year', label: 'Year' }
    ];
    /* GoVedia's placeholder option reads "--- Select Make ---" (its PromptMake,
       PromptModel and PromptYear settings; dashes of any kind around the words).
       The approved mock shows plain "Make", "Model", "Year", so the empty-value
       option is relabelled whenever GoVedia (re)builds a list. Only the text
       changes; the value stays "" and GoVedia never reads that text back. */
    var PROMPT_RE = /^[\s\-\u2010-\u2015]*select\s+(make|model|year)[\s\-\u2010-\u2015]*$/i;

    var barEl = null;
    var mmyEl = null;
    var pollTimer = null;
    var promptObserver = null;
    var pollStart = 0;
    var mmyObserver = null;
    var barObserver = null;
    var busyTimer = null;
    var savedSettled = false;   /* the saved decision is final: shown, or ruled out */
    var preRendered = false;    /* the saved line came from the URL tag at boot */
    var earlySaved = false;     /* snippets/auto-part-search.liquid drew the line from the cookie before GoVedia loaded */

    /* ---- small helpers ---------------------------------------------------- */
    function q(sel, root) { try { return (root || doc).querySelector(sel); } catch (e) { return null; } }
    function qa(sel, root) { try { return Array.prototype.slice.call((root || doc).querySelectorAll(sel)); } catch (e) { return []; } }
    function hasClass(el, c) { return !!(el && el.classList && el.classList.contains(c)); }
    function addClass(el, c) { if (el && el.classList) { el.classList.add(c); } }
    function removeClass(el, c) { if (el && el.classList) { el.classList.remove(c); } }
    function toggleClass(el, c, on) { if (on) { addClass(el, c); } else { removeClass(el, c); } }
    function trim(s) { return String(s == null ? '' : s).replace(/^\s+|\s+$/g, ''); }
    function matches(el, sel) {
      if (!el || el.nodeType !== 1) { return false; }
      var m = el.matches || el.msMatchesSelector || el.webkitMatchesSelector;
      try { return m ? m.call(el, sel) : false; } catch (e) { return false; }
    }
    function closest(el, sel) {
      while (el && el.nodeType === 1) {
        if (matches(el, sel)) { return el; }
        el = el.parentNode;
      }
      return null;
    }
    function contains(root, el) {
      if (!root || !el) { return false; }
      try { if (root.contains) { return root.contains(el); } } catch (e) { /* fall through */ }
      while (el) { if (el === root) { return true; } el = el.parentNode; }
      return false;
    }
    function isPhone() {
      try { return !!(win.matchMedia && win.matchMedia(PHONE_MQ).matches); } catch (e) { return false; }
    }
    function fire(el, type) {
      if (!el) { return; }
      var ev;
      try { ev = new win.Event(type, { bubbles: true, cancelable: true }); }
      catch (e) { ev = doc.createEvent('Event'); ev.initEvent(type, true, true); }
      el.dispatchEvent(ev);
    }
    function focusEl(el) {
      if (!el) { return; }
      try { el.focus({ preventScroll: false }); } catch (e) { try { el.focus(); } catch (e2) { /* nothing to focus */ } }
    }
    function isDisabled(input) { return !!(input && (input.disabled || input.hasAttribute('disabled'))); }
    function enable(input) {
      if (isDisabled(input)) { input.removeAttribute('disabled'); input.disabled = false; }
    }

    /* ---- finder lookups (every one guarded) ------------------------------- */
    function select(cls) { return mmyEl ? q('select.' + cls, mmyEl) : null; }
    function tabFor(step) {
      if (!mmyEl) { return null; }
      var t = q('.mmy-tab.' + step.tab, mmyEl);
      if (!t) {
        var s = q('select.' + step.cls, mmyEl);
        t = s ? closest(s, '.mmy-tab') : null;
      }
      return t;
    }
    function ops() { return mmyEl ? q('.mmy-tab.mmy-ops', mmyEl) : null; }
    function searchInput() { return mmyEl ? q('input.gv-btn-search', mmyEl) : null; }
    function clearInput() { return mmyEl ? q('input.gv-btn-clear', mmyEl) : null; }
    function savedEl() { return barEl ? q('.gv-saved', barEl) : null; }
    function value(sel) { return sel && typeof sel.value === 'string' ? sel.value : ''; }
    function selectedText(sel) {
      try {
        var o = sel.options[sel.selectedIndex];
        return o ? trim(o.text) : '';
      } catch (e) { return ''; }
    }
    function values() { return [value(select('mk')), value(select('md')), value(select('yr'))]; }
    function allFilled(v) { return !!(v[0] && v[1] && v[2]); }
    function anyFilled(v) { return !!(v[0] || v[1] || v[2]); }
    function isBound() {
      var mk = select('mk');
      return !!(searchInput() && mk && mk.options && mk.options.length > 1);
    }
    function touched() { return !!(barEl && barEl.getAttribute('data-rr-touched')); }
    function touch() { if (barEl) { barEl.setAttribute('data-rr-touched', '1'); } }

    /* ---- 2. bind step ------------------------------------------------------ */
    function makeClr(className, glyph, label) {
      var b = doc.createElement('button');
      b.type = 'button';
      b.className = className;
      var g = doc.createElement('span');
      g.className = 'gv-clr__glyph';
      g.setAttribute('aria-hidden', 'true');
      g.textContent = glyph;
      var l = doc.createElement('span');
      l.className = 'gv-clr__label';
      l.textContent = label;
      b.appendChild(g);
      b.appendChild(l);
      return b;
    }

    function bindStep() {
      var o = ops();
      if (!o) { return false; }
      if (q('.gv-go', o) || o.getAttribute('data-rr-bound') === '1') { return true; }
      var input = searchInput();
      var label = input ? trim(input.value) : '';
      if (!label || /^loading/i.test(label)) { label = 'Search'; }
      var go = doc.createElement('button');
      go.type = 'button';
      go.className = 'gv-go rr-button--primary';
      go.textContent = label;
      go.setAttribute('data-rr-label', label);
      go.addEventListener('click', function (e) { try { onSearchClick(e, go); } catch (err) { /* guarded */ } }, false);
      var clr = makeClr('gv-clr gv-clear', '✕', 'Clear');
      clr.addEventListener('click', function (e) { try { onClearClick(e); } catch (err) { /* guarded */ } }, false);
      o.appendChild(go);
      o.appendChild(clr);
      o.setAttribute('data-rr-bound', '1');
      return true;
    }

    /* ---- 3. step state ----------------------------------------------------- */
    function relabel() {
      if (!mmyEl) { return; }
      for (var i = 0; i < STEPS.length; i++) {
        var s = select(STEPS[i].cls);
        if (!s || !s.options) { continue; }
        /* 29 Sept audit: the selects had no accessible name (only the first option's text) */
        if (!s.getAttribute('aria-label')) { s.setAttribute('aria-label', 'Vehicle ' + STEPS[i].label.toLowerCase()); }
        for (var j = 0; j < s.options.length; j++) {
          var o = s.options[j];
          if (o.value === '' && PROMPT_RE.test(o.textContent || o.text || '')) { o.textContent = STEPS[i].label; }
        }
      }
    }

    function refresh() {
      if (!barEl || !mmyEl) { return; }
      relabel();
      var v = values();
      for (var i = 0; i < STEPS.length; i++) {
        var t = tabFor(STEPS[i]);
        if (!t) { continue; }
        toggleClass(t, 'is-done', !!v[i]);
        if (v[i]) { removeClass(t, 'is-miss'); }
        toggleClass(t, 'is-locked', i > 0 && !v[i - 1]);
      }
      toggleClass(barEl, 'has-pick', anyFilled(v));
      toggleClass(barEl, 'is-complete', allFilled(v));
    }

    function clearMiss() {
      var bad = qa('.mmy-tab.is-miss', mmyEl);
      for (var i = 0; i < bad.length; i++) { removeClass(bad[i], 'is-miss'); }
    }

    /* ---- 4. Search proxy --------------------------------------------------- */
    function setBusy(go, on) {
      if (!go) { return; }
      if (busyTimer) { win.clearTimeout(busyTimer); busyTimer = null; }
      if (on) {
        go.textContent = 'Loading';
        go.setAttribute('aria-busy', 'true');
        busyTimer = win.setTimeout(function () { try { setBusy(go, false); } catch (e) { /* guarded */ } }, BUSY_MS);
      } else {
        go.textContent = go.getAttribute('data-rr-label') || 'Search';
        go.removeAttribute('aria-busy');
      }
    }

    function onSearchClick(e, go) {
      if (e && e.preventDefault) { e.preventDefault(); }
      touch();
      clearMiss();
      var v = values();
      for (var i = 0; i < STEPS.length; i++) {
        if (!v[i]) {
          addClass(tabFor(STEPS[i]), 'is-miss');
          focusEl(select(STEPS[i].cls));
          return;
        }
      }
      var input = searchInput();
      if (!input) { return; }
      enable(input);        /* GoVedia enables it itself once all three are picked; this covers a click that lands first */
      setBusy(go, true);
      input.click();        /* native click: GoVedia's jQuery handler builds the tag, writes its cookies and navigates */
    }

    /* ---- 5. Clear proxy ---------------------------------------------------- */
    function hideSaved() {
      var s = savedEl();
      removeClass(barEl, 'is-saved');
      if (s) { s.setAttribute('hidden', ''); s.hidden = true; }
      preRendered = false;
      savedSettled = true;
      syncMini();
    }

    function manualReset() {
      var mk = select('mk');
      var md = select('md');
      var yr = select('yr');
      if (mk && value(mk)) { mk.value = ''; fire(mk, 'change'); }   /* GoVedia's handler empties the model and year lists */
      if (md && value(md)) { md.value = ''; fire(md, 'change'); }
      if (yr && value(yr)) { yr.value = ''; fire(yr, 'change'); }
      clearMiss();
      refresh();
      focusEl(mk);
    }

    function onClearClick(e) {
      if (e && e.preventDefault) { e.preventDefault(); }
      touch();
      var orphanTag = preRendered && !allFilled(values());   /* saved line from the URL, selects never filled */
      hideSaved();
      clearMiss();
      var input = clearInput();
      if (input && !isDisabled(input)) {
        input.click();      /* GoVedia: erase its cookies, then navigate or reset in place */
        refresh();          /* the in-place reset fires no change event */
        win.setTimeout(function () { try { refresh(); } catch (err) { /* guarded */ } }, 50);
        return;
      }
      manualReset();
      if (orphanTag && win.MMY_Collection === true && typeof win.MMY_Handle === 'string' && win.MMY_Handle) {
        win.location.href = '/collections/' + win.MMY_Handle;   /* the page still filters by the URL's vehicle */
      }
    }

    /* ---- 6. saved state ---------------------------------------------------- */
    /* The same normalization GoVedia's EncodeTag applies when it builds the
       URL segment from the option values, so a value with a space, "&", "."
       or "," compares equal to its tag. */
    function norm(s) {
      s = String(s == null ? '' : s);
      try { s = decodeURIComponent(s); } catch (e) { /* keep it as it is */ }
      return s.toLowerCase()
        .replace(/-fs-/g, '-')
        .replace(/[\s+&.,]+/g, '-')
        .replace(/"/g, '')
        .replace(/-{2,}/g, '-');
    }
    function vehicleTag(v) { return allFilled(v) ? norm(v[0] + '-' + v[1] + '-' + v[2]) : ''; }
    function lastSegment() {
      try {
        var p = String(win.location.pathname || '').replace(/\/+$/, '');
        return p.substring(p.lastIndexOf('/') + 1);
      } catch (e) { return ''; }
    }
    /* True only when the normalized tag names this exact vehicle: the three
       parts as one run (Make-Model-Year), or, for a store with a submodel step,
       "Make-Model-" somewhere in it with "-Year" as its last segment (the
       submodel sits between). Never the make and the year alone: on
       /collections/afe/Acura-ADX-2026 with the gv cookie holding 2026 Acura
       ILX, GoVedia pre-fills the selects with ILX and the line must keep
       saying ADX, the vehicle the page filters by. */
    function tagNames(t, v, tag) {
      if (!t) { return false; }
      if (t.indexOf(tag) !== -1) { return true; }
      var mm = norm(v[0] + '-' + v[1]) + '-';
      var yr = '-' + norm(v[2]);
      return mm.length > 1 && yr.length > 1 && t.indexOf(mm) !== -1 && t.slice(-yr.length) === yr;
    }
    function pageShowsVehicle(v) {
      var tag = vehicleTag(v);
      if (!tag) { return false; }
      if (win.MMY_Collection === true && typeof win.MMY_Tags === 'string' && /\S/.test(win.MMY_Tags)) {
        if (tagNames(norm(win.MMY_Tags), v, tag)) { return true; }
      }
      return tagNames(norm(lastSegment()), v, tag);
    }
    function showSaved(text) {
      var s = savedEl();
      var label = s ? q('.gv-saved__vehicle', s) : null;
      if (!s || !label || !barEl) { return false; }
      label.textContent = text;
      s.removeAttribute('hidden');
      s.hidden = false;
      addClass(barEl, 'is-saved');
      return true;
    }

    /* Results page: "Showing parts for" and no Shop parts; anywhere else the
       vehicle is only remembered, so the words change and Shop parts shows. */
    function setSavedMode(onResults) {
      var s = savedEl();
      if (!s) { return; }
      var lead = q('.gv-saved__lead', s);
      if (lead) { lead.textContent = onResults ? 'Showing parts that fit ' : 'Your vehicle: '; }
      var shop = q('.gv-saved__shop', s);
      if (shop) { shop.hidden = !!onResults; }
      var all = q('.gv-saved__all', s);
      if (all) { all.hidden = !onResults; }
      toggleClass(barEl, 'is-results', !!onResults);
      if (onResults) { fitPop(q('.gv-saved__check', s)); }
    }

    /* v7.1 motion review F7: the fitment check pops once (250ms) and gives
       one green pulse, then the class comes off, so hiding and showing the
       chip or the bar (the compact header, the phone fold) never replays it */
    function fitPop(el) {
      if (!el || el.rrPopped) { return; }
      el.rrPopped = true;
      addClass(el, 'rr-fitpop');
      el.addEventListener('animationend', function done(ev) {
        if (ev.animationName !== 'rr-vpulse') { return; }
        removeClass(el, 'rr-fitpop');
        el.removeEventListener('animationend', done);
      });
    }

    /* Show all parts (v2.17 6J): the same page without the vehicle segment,
       keeping the saved vehicle (GoVedia's cookie is untouched) */
    function showAllUrl() {
      var path = String(win.location.pathname || '').replace(/\/+$/, '');
      var seg = path.substring(path.lastIndexOf('/') + 1);
      var tag = typeof win.MMY_Tags === 'string' ? trim(win.MMY_Tags) : '';
      if (seg && tag && norm(seg) === norm(tag)) { path = path.substring(0, path.lastIndexOf('/')); }
      else if (win.MMY_Collection === true && typeof win.MMY_Handle === 'string' && win.MMY_Handle) { path = '/collections/' + win.MMY_Handle; }
      var qs = String(win.location.search || '').replace(/([?&])page=\d+(&|$)/, '$1').replace(/[?&]$/, '');
      return (path || '/') + qs;
    }
    function onShowAll(e) {
      if (e && e.preventDefault) { e.preventDefault(); }
      win.location.href = showAllUrl();
    }

    function maybeShowSaved() {
      if (savedSettled) { return; }
      if (touched()) { savedSettled = true; return; }
      if (!isBound()) { return; }
      var v = values();
      if (!allFilled(v)) {
        /* a line drawn early from a stale cookie goes once GoVedia shows no vehicle */
        if (earlySaved && !preRendered) { hideSaved(); }
        savedSettled = true;
        return;
      }   /* a pre-rendered line stays: the URL still names its vehicle */
      var onResults = pageShowsVehicle(v);
      if (preRendered && !onResults) { savedSettled = true; return; }   /* the URL's vehicle wins over the cookie's */
      showSaved(trim(selectedText(select('yr')) + ' ' + selectedText(select('mk')) + ' ' + selectedText(select('md'))));
      setSavedMode(onResults);
      savedSettled = true;
      updateGarage();
      syncMini();
    }

    function readVehicleCookie() {
      try {
        var parts = String(doc.cookie || '').split(';');
        var key = VEHICLE_COOKIE + '=';
        for (var i = 0; i < parts.length; i++) {
          var c = trim(parts[i]);
          if (c.indexOf(key) !== 0) { continue; }
          var raw = c.substring(key.length);
          var obj = null;
          try { obj = JSON.parse(raw); } catch (e1) { try { obj = JSON.parse(decodeURIComponent(raw)); } catch (e2) { obj = null; } }
          var m = obj && obj.mmy;
          if (m && typeof m === 'object' && m.make && m.model && m.year) {
            return { make: String(m.make), model: String(m.model), year: String(m.year) };
          }
        }
      } catch (e) { /* no cookie access */ }
      return null;
    }

    function preRenderFromTag() {
      if (win.MMY_Collection !== true || typeof win.MMY_Tags !== 'string') { return; }
      var tag = trim(win.MMY_Tags).replace(/^ymm_/i, '');
      var m = /^(.+)-(\d{4})$/.exec(tag);
      if (!m) { return; }
      var text = '';
      var ck = readVehicleCookie();
      if (ck && ck.year === m[2] && norm(ck.make + '-' + ck.model + '-' + ck.year) === norm(tag)) {
        text = ck.year + ' ' + ck.make + ' ' + ck.model;
      } else {
        var rest = m[1];
        var cut = rest.indexOf('-');
        var make = cut > 0 ? rest.substring(0, cut) : rest;
        var model = cut > 0 ? rest.substring(cut + 1) : '';
        text = trim(m[2] + ' ' + make + ' ' + model);
      }
      if (showSaved(text)) { preRendered = true; setSavedMode(true); }
    }

    function onChangeVehicle(e) {
      if (e && e.preventDefault) { e.preventDefault(); }
      touch();
      hideSaved();
      refresh();
      if (isPhone()) { addClass(barEl, 'gv-expanded'); }
      focusEl(select('mk'));
    }

    /* ---- 9. garage ------------------------------------------------------- */
    var GARAGE_PENDING_KEY = 'rr-garage-pending';
    var GARAGE_PENDING_MS = 30 * 60 * 1000;
    var STATUS_MS = 10000;
    var garageObserver = null;
    var statusTimer = null;
    var lastGvStatus = '';

    function signedIn() { return /^[1-9][0-9]*$/.test(String(win.MMY_Customer == null ? '' : win.MMY_Customer)); }
    function garageMount() { return q('#gv-save-vehicles'); }
    function garageAddLink() { var m = garageMount(); return m ? q('a.add-vehicle', m) : null; }
    function garageUrl() {
      var s = savedEl();
      return (s && s.getAttribute('data-rr-garage')) || '/pages/my-garage';
    }
    function loginUrl() {
      var s = savedEl();
      var base = (s && s.getAttribute('data-rr-login')) || '/account/login';
      var back = String(win.location.pathname || '/') + String(win.location.search || '');
      return base + (base.indexOf('?') === -1 ? '?' : '&') + 'return_to=' + encodeURIComponent(back);
    }
    function vehicleKey(make, model, year) {
      return [make, model, year].map(function (x) { return trim(x).toUpperCase(); }).join('|');
    }
    function currentKey() {
      var v = values();
      return allFilled(v) ? vehicleKey(v[0], v[1], v[2]) : '';
    }
    function inGarage() {
      var key = currentKey();
      var m = garageMount();
      if (!key || !m) { return false; }
      var links = qa('a.vehicle-link[json]', m);
      for (var i = 0; i < links.length; i++) {
        try {
          var o = JSON.parse(links[i].getAttribute('json'));
          if (o && vehicleKey(o.make, o.model, o.year) === key) { return true; }
        } catch (e) { /* not a vehicle */ }
      }
      return false;
    }

    function statusNodes() {
      var out = [];
      var a = barEl ? q('.gv-status', barEl) : null;
      if (a) { out.push(a); }
      var b = mini ? q('.rr-vmini__status', mini) : null;
      if (b) { out.push(b); }
      return out;
    }
    /* text, then an optional link; sticky messages (the sign-in prompt) stay */
    function setStatus(text, link, sticky) {
      var nodes = statusNodes();
      if (statusTimer) { win.clearTimeout(statusTimer); statusTimer = null; }
      for (var i = 0; i < nodes.length; i++) {
        var n = nodes[i];
        while (n.firstChild) { n.removeChild(n.firstChild); }
        if (!text) { n.hidden = true; continue; }
        n.appendChild(doc.createTextNode(text));
        if (link && link.href) {
          n.appendChild(doc.createTextNode(' '));
          var a = doc.createElement('a');
          a.href = link.href;
          a.textContent = link.text;
          n.appendChild(a);
        }
        n.hidden = false;
      }
      if (text && !sticky) {
        statusTimer = win.setTimeout(function () { try { setStatus(''); } catch (e) { /* guarded */ } }, STATUS_MS);
      }
      syncMini();
    }

    function updateGarage() {
      var saved = inGarage();
      var btns = qa('.gv-saved__garage').concat(mini ? qa('.rr-vmini__garage', mini) : []);
      for (var i = 0; i < btns.length; i++) {
        var b = btns[i];
        var lg = q('.gv-garage__long', b);
        var sh = q('.gv-garage__short', b);
        var gl = q('.gv-clr__glyph', b) || q('.rr-vmini__glyph', b);
        if (lg) { lg.textContent = saved ? 'In My Garage' : 'Save to My Garage'; }
        if (sh) { sh.textContent = saved ? 'Saved' : 'Save'; }
        if (gl) { gl.textContent = saved ? '✓' : '+'; }
        b.setAttribute('data-rr-in', saved ? '1' : '0');
        b.setAttribute('aria-label', saved ? 'In My Garage, open My Garage' : 'Save this vehicle to My Garage');
      }
    }

    function readPending() {
      try {
        var raw = win.localStorage.getItem(GARAGE_PENDING_KEY);
        if (!raw) { return null; }
        var o = JSON.parse(raw);
        if (!o || !o.k || !o.t || (Date.now() - o.t) > GARAGE_PENDING_MS) {
          win.localStorage.removeItem(GARAGE_PENDING_KEY);
          return null;
        }
        return o;
      } catch (e) { return null; }
    }
    function dropPending() {
      try { win.localStorage.removeItem(GARAGE_PENDING_KEY); } catch (e) { /* storage blocked */ }
    }
    function resumePending() {
      if (!signedIn()) { return; }
      var o = readPending();
      if (!o) { return; }
      if (o.k !== currentKey()) { return; }
      if (inGarage()) { dropPending(); updateGarage(); return; }
      var add = garageAddLink();
      if (!add) { return; }   /* GoVedia has not drawn the garage yet; the observer calls again */
      dropPending();
      add.click();
    }

    function onGarageClick(e) {
      if (e && e.preventDefault) { e.preventDefault(); }
      var btn = e && e.target ? closest(e.target, 'button') : null;
      if (btn && btn.getAttribute('data-rr-in') === '1') { win.location.href = garageUrl(); return; }
      var v = values();
      if (!allFilled(v)) { setStatus('Pick a make, model and year first.'); return; }
      if (!signedIn()) {
        try {
          win.localStorage.setItem(GARAGE_PENDING_KEY, JSON.stringify({ k: vehicleKey(v[0], v[1], v[2]), t: Date.now() }));
        } catch (err) { /* storage blocked: they can press Save again after signing in */ }
        setStatus('Sign in to save this vehicle to My Garage.', { href: loginUrl(), text: 'Sign in' }, true);
        return;
      }
      var add = garageAddLink();
      if (!add) { setStatus('My Garage is still loading. Try again in a moment.'); return; }
      add.click();   /* GoVedia: reads the bar's selects, saves, re-renders its list, writes its status */
    }

    /* GoVedia writes "Saving vehicle...", "Vehicle saved." and its errors into
       .gv-garage-status inside the mount, and re-renders the vehicle list there */
    function onGarageMutation() {
      var m = garageMount();
      var st = m ? q('.gv-garage-status', m) : null;
      var text = st && !st.hidden ? trim(st.textContent) : '';
      if (text && text !== lastGvStatus) {
        var saved = /saved\.?$/i.test(text) && !/not/i.test(text);
        setStatus(text, saved ? { href: garageUrl(), text: 'View My Garage' } : null);
      }
      lastGvStatus = text;
      updateGarage();
      resumePending();
    }
    function watchGarage() {
      var m = garageMount();
      if (!m || garageObserver || !win.MutationObserver) { return; }
      garageObserver = new win.MutationObserver(function () { try { onGarageMutation(); } catch (e) { /* guarded */ } });
      garageObserver.observe(m, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['hidden'] });
      doc.addEventListener('gv:garage-updated', function () { try { updateGarage(); } catch (e) { /* guarded */ } }, false);
      onGarageMutation();
    }

    /* ---- 10. compact bar: retired 29 Sept (the chip and car icon replaced
       .rr-vmini); its builder and scroll watcher were removed in the v7.1
       motion review (F10). mini stays null, so the guarded syncs below no-op. */
    var mini = null;
    /* vehicle icon, spec v2.18 6J option B (snippets/rr-icon-vehicle.liquid); the car icon and chip use it */
    var MINI_ICON = '<svg class="rr-icon-vehicle" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M4.5 16.5H3a1 1 0 0 1-1-1v-2.2a1.6 1.6 0 0 1 1.2-1.5L7 11l3.2-2.7A3 3 0 0 1 12.1 7.5H15a3 3 0 0 1 2.3 1.1l2 2.4 1.7.5a1.4 1.4 0 0 1 1 1.3v2.7a1 1 0 0 1-1 1h-1.5M15 16.5H8.5"/><path d="M9.6 11h7.9" stroke-width="1.6"/><circle cx="6.5" cy="16.5" r="2"/><circle cx="17" cy="16.5" r="2"/></svg>';
    function syncMini() {
      if (!mini || !barEl) { return; }
      var saved = hasClass(barEl, 'is-saved');
      var results = hasClass(barEl, 'is-results');
      var label = savedEl() ? q('.gv-saved__vehicle', savedEl()) : null;
      mini.setAttribute('data-state', saved ? 'saved' : 'empty');
      toggleClass(mini, 'is-results', saved && results);
      var lead = q('.rr-vmini__lead', mini);
      if (lead) { lead.textContent = saved ? (results ? 'Showing parts for' : 'Your vehicle') : 'Shop by vehicle'; }
      var veh = q('.rr-vmini__vehicle', mini);
      if (veh) { veh.textContent = saved && label ? label.textContent : ''; }
    }

    function focusQuiet(el) {
      if (!el) { return; }
      try { el.focus({ preventScroll: true }); } catch (e) { try { el.focus(); } catch (e2) { /* nothing to focus */ } }
    }
    function reducedMotion() {
      try { return !!(win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) { return false; }
    }
    /* the lowest bottom edge of a fixed or sticky header strip on screen */
    function headerBottom() {
      var sels = ['#mobile-header', '.header-section', '.announcement-bar__container'];
      var best = 0;
      for (var i = 0; i < sels.length; i++) {
        var el = q(sels[i]);
        if (!el || !el.offsetHeight) { continue; }
        var pos = '';
        try { pos = win.getComputedStyle(el).position; } catch (e) { pos = ''; }
        if (pos !== 'fixed' && pos !== 'sticky') { continue; }
        var r = el.getBoundingClientRect();
        if (r.bottom > 0 && r.top < 240 && r.bottom > best) { best = r.bottom; }
      }
      return Math.round(best);
    }
    function scrollToBar(done) {
      if (!barEl) { return; }
      var y = (win.pageYOffset || 0) + barEl.getBoundingClientRect().top - headerBottom() - 12;
      if (y < 0) { y = 0; }
      var smooth = !reducedMotion();
      try { win.scrollTo({ top: y, behavior: smooth ? 'smooth' : 'auto' }); } catch (e) { win.scrollTo(0, y); }
      win.setTimeout(function () { try { if (done) { done(); } } catch (e) { /* guarded */ } }, smooth ? 450 : 0);
    }
    /* ---- 11. compact header: vehicle chip, car icon, dropdown, sheet, fold
       (brand spec v2.16 6D, references sticky-header-compact.html and
       vehicle-bar-morph.html, 28 Sept 2026). assets/rr-compact.js owns the
       compact state and fires rr:compact-before / rr:compact.
       Desktop: button.rr-vchip in the header row ("✓ YEAR MAKE MODEL ▾" or
       "Select vehicle ▾") drops this bar under the compact header as a panel
       (.rr-vdrop); a placeholder holds its place in the flow. Esc, a click
       outside or leaving compact closes it; focus goes to Make and back to
       the chip. The saved line gives way to the pre-filled fields while the
       panel is open and comes back on close if nothing was changed.
       Phones: button.rr-mvico (car, green ✓ badge with a saved vehicle) in
       the header row. On entering compact the bar folds into it (FLIP with
       the Web Animations API, transform and opacity only, 560ms; the car
       icon pops at 440ms; the bar's space collapses over 300ms); leaving
       compact plays it in reverse (420ms). Tapping the icon slides the bar
       down from the top as a sheet (.rr-vsheet) with the stacked fields.
       Reduced motion, a first compact on load, or a bar that is off screen:
       instant swap. The 28 Sept compact bar (.rr-vmini) is retired. */
    var chip = null;
    var vicon = null;
    var dropOpen = false;
    var sheetOpen = false;
    var placeholder = null;
    var restoreSaved = false;
    var folded = false;
    var flying = false;
    var foldTarget = null;
    var unfoldRect = null;
    var userScrolled = false;
    var scrim = null;
    var CAR_SVG = MINI_ICON.replace('width="20" height="20"', 'width="22" height="22"');

    function savedText() {
      var s = savedEl();
      var b = s ? q('.gv-saved__vehicle', s) : null;
      return hasClass(barEl, 'is-saved') && b ? trim(b.textContent) : '';
    }
    function rememberedText() {
      /* the chip keeps naming the vehicle while the panel shows the fields */
      return savedText() || (restoreSaved && savedEl() ? trim((q('.gv-saved__vehicle', savedEl()) || {}).textContent || '') : '');
    }
    /* v2.17 (spec 6J): the green check means the products on this page are
       filtered to the vehicle. 'none': no vehicle; 'saved': a vehicle is
       remembered but the page shows everything; 'fit': the results are
       filtered to it. */
    var CHECK_SVG = '<svg viewBox="0 0 12 12" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M2.5 6.2 5 8.5l4.5-5"/></svg>';
    function vehicleState() {
      var t = rememberedText();
      if (!t) { return 'none'; }
      return hasClass(barEl, 'is-results') ? 'fit' : 'saved';
    }
    function setChip(el, text, withCaret, state) {
      while (el.firstChild) { el.removeChild(el.firstChild); }
      if (state === 'fit') {
        var d = doc.createElement('span');
        d.className = 'rr-vchip__dot';
        d.setAttribute('aria-hidden', 'true');
        d.innerHTML = CHECK_SVG;
        fitPop(d);
        var w = doc.createElement('span');
        w.className = 'rr-vchip__txt';
        w.appendChild(doc.createTextNode('Fits '));
        var b = doc.createElement('b');
        b.textContent = text;
        w.appendChild(b);
        el.appendChild(d);
        el.appendChild(w);
      } else {
        var c = doc.createElement('span');
        c.className = 'rr-vchip__car';
        c.setAttribute('aria-hidden', 'true');
        c.innerHTML = CAR_SVG;
        var l = doc.createElement('span');
        l.className = 'rr-vchip__txt';
        if (state === 'saved') {
          l.appendChild(doc.createTextNode(text + ' '));
          var sm = doc.createElement('small');
          sm.textContent = '· All parts';
          l.appendChild(sm);
        } else {
          l.textContent = 'Select vehicle';
        }
        el.appendChild(c);
        el.appendChild(l);
      }
      if (withCaret) {
        var k = doc.createElement('span');
        k.className = 'rr-vchip__caret';
        k.setAttribute('aria-hidden', 'true');
        el.appendChild(k);
      }
    }
    function syncChip() {
      var t = rememberedText();
      var st = vehicleState();
      if (chip) {
        if (chip.getAttribute('data-rr-state') !== st || chip.getAttribute('data-rr-v') !== t) {
          setChip(chip, t, true, st);
          chip.setAttribute('data-rr-state', st);
          chip.setAttribute('data-rr-v', t);
          primeSlots();   /* the chip's width follows its label */
        }
        chip.setAttribute('aria-label', st === 'fit' ? 'Showing parts that fit ' + t : (t ? 'Your vehicle: ' + t + ', all parts' : 'Select vehicle'));
      }
      if (vicon) {
        toggleClass(vicon, 'has-vehicle', !!t);
        toggleClass(vicon, 'is-fit', st === 'fit');
        vicon.setAttribute('aria-label', st === 'fit' ? 'Showing parts that fit ' + t : (t ? 'Your vehicle: ' + t : 'Select vehicle'));
      }
      syncResults();
    }

    /* ---- 13. above the products (v2.17 spec 6J), collection pages only ----
       Saved, not filtered: a light grey line with a link that filters this
       collection. Filtered: the count line and a "Fits ... x" chip first in
       the active-filter row (removing it = Show all parts). Filtered with no
       results: sections/collection__main.liquid prints the empty state; the
       vehicle name there is corrected from the bar. All text via
       textContent / createElement (the theme's inline-script minifier and
       XCloud's re-renders never touch these nodes). */
    var resultsState = '';
    function collectionMain() { return q('[data-collection-main]'); }
    function noun(main) {
      var n = main ? trim(main.getAttribute('data-rr-noun') || '') : '';
      return n || 'parts';
    }
    function mk(tag, cls, txt) {
      var e = doc.createElement(tag);
      if (cls) { e.className = cls; }
      if (txt != null) { e.textContent = txt; }
      return e;
    }
    function dropNode(sel) {
      qa(sel).forEach(function (n) { if (n.parentNode) { n.parentNode.removeChild(n); } });
    }
    function gridBefore(main) {
      var w = q('.collection-matrix__wrapper', main) || q('#cloud_search_filters_root', main) || q('.rr-vempty', main);
      return w && w.parentNode === main ? w : (main.firstChild || null);
    }
    function fitChip(text) {
      var c = mk('button', 'rr-fitchip');
      c.type = 'button';
      c.setAttribute('aria-label', 'Remove filter: fits ' + text + ' (show all parts)');
      var d = mk('span', 'rr-fitchip__dot');
      d.setAttribute('aria-hidden', 'true');
      d.innerHTML = CHECK_SVG;
      c.appendChild(d);
      c.appendChild(doc.createTextNode('Fits ' + text));
      var x = mk('span', 'rr-fitchip__x', '✕');
      x.setAttribute('aria-hidden', 'true');
      c.appendChild(x);
      c.addEventListener('click', onShowAll, false);
      return c;
    }
    function placeFitChip(text) {
      var row = q('.rr-active-filters');
      var main = collectionMain();
      if (!row && main) {
        row = mk('div', 'rr-active-filters rr-active-filters--fit');
        row.setAttribute('data-rr-enhanced', '1');
        row.appendChild(mk('span', 'rr-active-filters__label', 'Active filters'));
        var box = q('.rr-vfit', main);
        if (box) { box.appendChild(row); } else { main.insertBefore(row, gridBefore(main)); }
      }
      if (!row) { return; }
      var old = q('.rr-fitchip', row);
      if (old && old.getAttribute('data-rr-v') === text) { return; }
      if (old) { old.parentNode.removeChild(old); }
      var chipEl = fitChip(text);
      chipEl.setAttribute('data-rr-v', text);
      var label = q('.rr-active-filters__label', row);
      row.insertBefore(chipEl, label ? label.nextSibling : row.firstChild);
    }
    function syncResults() {
      var main = collectionMain();
      if (!main || !barEl) { return; }
      var st = vehicleState();
      var t = rememberedText();
      var count = parseInt(String(main.getAttribute('data-rr-results-count') || '').replace(/[^0-9]/g, ''), 10);
      var empty = !!q('.rr-vempty', main);
      var key = st + '|' + t + '|' + empty;
      if (key === resultsState && (st !== 'fit' || q('.rr-fitchip'))) { return; }
      resultsState = key;
      dropNode('.rr-vnudge');
      if (st !== 'fit') { dropNode('.rr-vfit'); dropNode('.rr-fitchip'); dropNode('.rr-active-filters--fit'); }
      var n = noun(main);
      if (st === 'saved' && !empty) {
        var nudge = mk('div', 'rr-vnudge');
        var ic = mk('span', 'rr-vnudge__car');
        ic.setAttribute('aria-hidden', 'true');
        ic.innerHTML = CAR_SVG;
        nudge.appendChild(ic);
        var p = mk('p', 'rr-vnudge__txt', 'These are all ' + n + ', not only ones that fit your ' + t + '. ');
        var a = mk('a', 'rr-vnudge__go', 'Show ' + n + ' that fit');
        a.href = '#';
        a.addEventListener('click', function (e) {
          var shop = savedEl() ? q('.gv-saved__shop', savedEl()) : null;
          if (shop) { onSearchClick(e, shop); } else if (e && e.preventDefault) { e.preventDefault(); }
        }, false);
        p.appendChild(a);
        nudge.appendChild(p);
        main.insertBefore(nudge, gridBefore(main));
      }
      if (st === 'fit') {
        var box = q('.rr-vfit', main);
        if (!box) {
          box = mk('div', 'rr-vfit');
          main.insertBefore(box, gridBefore(main));
        }
        var line = q('.rr-vfit__count', box);
        if (!empty && count === count) {
          if (!line) { line = mk('p', 'rr-vfit__count'); box.insertBefore(line, box.firstChild); }
          while (line.firstChild) { line.removeChild(line.firstChild); }
          line.appendChild(mk('b', '', String(count).replace(/\B(?=(\d{3})+(?!\d))/g, ',')));
          line.appendChild(doc.createTextNode(' ' + (count === 1 ? n.replace(/s$/, '') : n) + ' ' + (count === 1 ? 'fits' : 'fit') + ' your ' + t));
        } else if (line) { line.parentNode.removeChild(line); }
        placeFitChip(t);
        qa('[data-rr-vname]', main).forEach(function (el) { el.textContent = t; });
      }
    }
    function watchActiveRow() {
      /* rr-filters.js may build its own row after this ran, or XCloud may
         re-render it: keep the Fits chip first */
      if (!win.MutationObserver) { return; }
      var main = collectionMain();
      if (!main) { return; }
      var mo = new win.MutationObserver(function () {
        try {
          if (vehicleState() !== 'fit') { return; }
          var row = q('.rr-active-filters');
          if (row && !q('.rr-fitchip', row)) {
            dropNode('.rr-fitchip');
            var stray = q('.rr-active-filters--fit');
            if (stray && stray !== row && !q('.cloud-search-applied-filter', stray) && stray.parentNode) { stray.parentNode.removeChild(stray); }
            placeFitChip(rememberedText());
          }
        } catch (err) { /* guarded */ }
      });
      mo.observe(main, { childList: true, subtree: true });
    }
    function bindEmpty() {
      var main = collectionMain();
      var ch = main ? q('[data-rr-vchange]', main) : null;
      if (!ch) { return; }
      ch.addEventListener('click', function (e) {
        e.preventDefault();
        if (isPhone()) {
          scrollToBar(function () { onChangeVehicle(); });
        } else if (hasClass(doc.documentElement, 'rr-compact') && chip) {
          openDrop();
        } else {
          scrollToBar(function () { onChangeVehicle(); });
        }
      }, false);
    }

    function buildChip() {
      var row = q('#header .header__inner-content');
      if (!row) { return; }
      chip = q('.rr-vchip', row);
      if (chip) { return; }
      chip = doc.createElement('button');
      chip.type = 'button';
      chip.className = 'rr-vchip';
      chip.setAttribute('aria-expanded', 'false');
      chip.setAttribute('aria-haspopup', 'true');
      var icons = q('.header__icons', row);
      row.insertBefore(chip, icons || null);
      chip.addEventListener('click', function (e) {
        e.preventDefault();
        if (dropOpen) { closeDrop(true); } else { openDrop(); }
      }, false);
    }
    function buildVicon() {
      var host = q('#mobile-header .mobile-icons');
      if (!host) { return; }
      vicon = q('.rr-mvico', host);
      if (vicon) { return; }
      vicon = doc.createElement('button');
      vicon.type = 'button';
      vicon.className = 'rr-mvico rr-mib';
      vicon.setAttribute('aria-expanded', 'false');
      vicon.innerHTML = CAR_SVG + '<span class="rr-mvico__bd" aria-hidden="true">' + CHECK_SVG + '</span>';
      var cart = q('.header-cart', host);
      host.insertBefore(vicon, cart || null);
      vicon.addEventListener('click', function (e) {
        e.preventDefault();
        if (sheetOpen) { closeSheet(true); } else { openSheet(); }
      }, false);
    }

    function fieldsOn() {
      restoreSaved = hasClass(barEl, 'is-saved');
      if (restoreSaved) {
        removeClass(barEl, 'is-saved');
        var s = savedEl();
        if (s) { s.hidden = true; s.setAttribute('hidden', ''); }
      }
      refresh();
    }
    function fieldsOff() {
      if (restoreSaved && !touched()) {
        var s = savedEl();
        if (s) { s.hidden = false; s.removeAttribute('hidden'); }
        addClass(barEl, 'is-saved');
      }
      restoreSaved = false;
      syncChip();
    }

    /* desktop panel */
    function openDrop() {
      if (dropOpen || !barEl || isPhone()) { return; }
      dropOpen = true;
      var cs = win.getComputedStyle(barEl);
      placeholder = doc.createElement('div');
      placeholder.className = 'rr-vbar-ph';
      placeholder.style.height = barEl.offsetHeight + 'px';
      placeholder.style.marginTop = cs.marginTop;
      placeholder.style.marginBottom = cs.marginBottom;
      barEl.parentNode.insertBefore(placeholder, barEl);
      fieldsOn();
      removeClass(barEl, 'rr-vdfolded');
      var h = q('#header');
      var top = h ? Math.round(h.getBoundingClientRect().bottom) : headerBottom();
      barEl.style.setProperty('--rr-vdrop-top', top + 'px');
      addClass(barEl, 'rr-vdrop');
      void barEl.offsetWidth;
      addClass(barEl, 'is-on');
      if (chip) { chip.setAttribute('aria-expanded', 'true'); }
      win.setTimeout(function () { focusQuiet(select('mk')); }, reducedMotion() ? 0 : 260);
    }
    function closeDrop(returnFocus) {
      if (!dropOpen) { return; }
      dropOpen = false;
      removeClass(barEl, 'is-on');
      if (chip) { chip.setAttribute('aria-expanded', 'false'); }
      var done = function () {
        removeClass(barEl, 'rr-vdrop');
        barEl.style.removeProperty('--rr-vdrop-top');
        if (placeholder && placeholder.parentNode) { placeholder.parentNode.removeChild(placeholder); }
        placeholder = null;
        fieldsOff();
        /* 29 Sept: the desktop bar is never folded away any more (deskFold) */
      };
      if (reducedMotion()) { done(); } else { win.setTimeout(done, 260); }
      if (returnFocus && chip) { focusQuiet(chip); }
    }

    /* phone sheet */
    function scrimEl() {
      if (!scrim) {
        scrim = doc.createElement('div');
        scrim.className = 'rr-vscrim';
        scrim.addEventListener('click', function () { closeSheet(true); }, false);
        doc.body.appendChild(scrim);
      }
      return scrim;
    }
    function sheetClose() {
      var c = barEl ? q('.rr-vsheet__x', barEl) : null;
      if (!c && barEl) {
        c = doc.createElement('button');
        c.type = 'button';
        c.className = 'rr-vsheet__x';
        c.setAttribute('aria-label', 'Close');
        c.textContent = '✕';
        c.addEventListener('click', function (e) { e.preventDefault(); closeSheet(true); }, false);
        var cont = q('.container', barEl) || barEl;
        cont.appendChild(c);
      }
      return c;
    }
    function openSheet() {
      if (sheetOpen || !barEl || !isPhone()) { return; }
      sheetOpen = true;
      fieldsOn();
      sheetClose();
      addClass(barEl, 'gv-expanded');
      addClass(barEl, 'rr-vsheet');
      addClass(scrimEl(), 'is-on');
      void barEl.offsetWidth;
      addClass(barEl, 'is-on');
      addClass(doc.documentElement, 'rr-vsheet-open');
      if (vicon) { vicon.setAttribute('aria-expanded', 'true'); }
      /* phones: focus the sheet's close button, not Make; focusing a select
         under 16px zooms the page on iOS Safari */
      win.setTimeout(function () { focusQuiet(q('.rr-vsheet__x', barEl)); }, reducedMotion() ? 0 : 320);
    }
    function closeSheet(returnFocus) {
      if (!sheetOpen) { return; }
      sheetOpen = false;
      removeClass(barEl, 'is-on');
      if (scrim) { removeClass(scrim, 'is-on'); }
      removeClass(doc.documentElement, 'rr-vsheet-open');
      if (vicon) { vicon.setAttribute('aria-expanded', 'false'); }
      var done = function () {
        removeClass(barEl, 'rr-vsheet');
        removeClass(barEl, 'gv-expanded');
        fieldsOff();
      };
      if (reducedMotion()) { done(); } else { win.setTimeout(done, 320); }
      if (returnFocus && vicon) { focusQuiet(vicon); }
    }

    /* the fold */
    function iconSlot() {
      if (!vicon) { return null; }
      var r = vicon.getBoundingClientRect();
      var cx = r.left + r.width / 2;
      var cy = r.top + r.height / 2;
      return { left: cx - 17, top: cy - 17, width: 34, height: 34 };
    }
    function flip(b, i) {
      return 'translate(' + (i.left - b.left) + 'px,' + (i.top - b.top) + 'px) scale(' + (i.width / b.width) + ',' + (i.height / b.height) + ')';
    }
    /* v2.17 smoothing (owner, 28 Sept: "too fast and abrupt"): the shape that
       flies is a copy of the bar itself (ids and names stripped, inert), so
       the fold reads as the bar shrinking into its target while its fields
       fade out, not as the bar swapping to a black box. */
    /* v7.1 motion review D5 (owner, 4 Oct): 450ms both ways (the guide's
       longest timing, still slower than the 300ms header) and every part of
       the flight on the brand curve, so it lands as one motion */
    var FOLD_MS = 450;
    var FOLD_EASE = 'cubic-bezier(.2,.8,.2,1)';
    var UNFOLD_MS = 450;
    /* D5: the copy is built in idle time and reused (ghostReady), so the
       fold frame no longer deep-clones the bar while the page scrolls. Any
       change inside the bar drops the copy and an idle rebuild follows. */
    var ghostReady = null;
    var ghostPrepQueued = false;
    function prepGhost() {
      if (ghostReady || ghostPrepQueued || !barEl || !isPhone()) { return; }
      ghostPrepQueued = true;
      var go = function () {
        ghostPrepQueued = false;
        if (!ghostReady && !flying && isPhone()) { try { ghostReady = buildGhost(); } catch (e) { ghostReady = null; } }
      };
      if (win.requestIdleCallback) { win.requestIdleCallback(go, { timeout: 2000 }); } else { win.setTimeout(go, 300); }
    }
    function dropGhost() {
      ghostReady = null;
      prepGhost();
    }
    function ghost(r, onPage) {
      var g = ghostReady || buildGhost();
      ghostReady = null;
      g.style.position = '';
      /* the return trip lands on the bar in the page, so it is anchored to
         the page (absolute): it keeps landing on the bar even while the page
         is still scrolling */
      if (onPage) {
        g.style.position = 'absolute';
        g.style.left = (r.left + (win.pageXOffset || 0)) + 'px';
        g.style.top = (r.top + (win.pageYOffset || 0)) + 'px';
      } else {
        g.style.left = r.left + 'px';
        g.style.top = r.top + 'px';
      }
      g.style.width = r.width + 'px';
      g.style.height = r.height + 'px';
      doc.body.appendChild(g);
      return g;
    }
    function buildGhost() {
      var src = q('.container', barEl);
      var g = doc.createElement('div');
      g.className = 'rr-vghost';
      g.setAttribute('aria-hidden', 'true');
      if (src) {
        var c = src.cloneNode(true);
        /* ids go, except #gv-mmy: govedia-ov.css lays the fields out through it,
           and the copy sits at the end of <body>, so getElementById and
           jQuery still find the real one first (the copy lives 760ms) */
        qa('[id],[name]', c).forEach(function (n) { if (n.id !== 'gv-mmy') { n.removeAttribute('id'); } n.removeAttribute('name'); });
        qa('input,select,button,textarea,a', c).forEach(function (n) { n.setAttribute('tabindex', '-1'); });
        g.setAttribute('inert', '');
        qa('.gv-status,.rr-gv-garage-mount,script', c).forEach(function (n) { if (n.parentNode) { n.parentNode.removeChild(n); } });
        c.className = src.className + ' rr-vghost__in';
        var wrap = doc.createElement('div');
        wrap.className = barEl.className.replace(/\b(rr-vfolded|rr-vdfolded|rr-vfade|rr-vdrop|is-on|gv-expanded|rr-vsheet)\b/g, '') + ' rr-vghost__bar';
        wrap.appendChild(c);
        g.appendChild(wrap);
      }
      return g;
    }
    function morph(g, from, to, round, done) {
      var inner = q('.rr-vghost__in', g);
      if (inner && inner.animate) {
        /* the boxes that paint (.gv-body and .gv-inner are display: contents
           on desktop, and opacity does nothing on those) */
        qa('.gv-prompt, .gv-prompt-mobile, #gv-mmy, .gv-saved, .gv-status', inner).forEach(function (k) {
          k.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 150, easing: FOLD_EASE, fill: 'forwards' });
        });
      }
      /* transform and opacity only, so the whole flight stays on the
         compositor (animating the corner radius repainted every frame, which
         is what made the landing stutter). The move eases all the way in;
         the copy fades over its last 45% while the target fades in under it. */
      var end = flip(from, to);
      var a = g.animate([
        { transform: 'none' },
        { transform: end }
      ], { duration: FOLD_MS, easing: FOLD_EASE, fill: 'forwards' });
      var fd = g.animate([
        { opacity: 1, offset: 0 },
        { opacity: 1, offset: 0.55, easing: FOLD_EASE },
        { opacity: 0, offset: 1 }
      ], { duration: FOLD_MS, fill: 'forwards' });
      fd.onfinish = fd.oncancel = function () { if (g.parentNode) { g.parentNode.removeChild(g); } if (done) { done(); } };
      return a;
    }

    /* the reverse (owner, 28 Sept: "works one way only, no continuity"): the
       copy grows out of the chip or the car icon back to the bar, the fields
       fading in over the last part, then the real bar takes over under it */
    function unmorph(g, to, from, done) {
      var inner = q('.rr-vghost__in', g);
      if (inner && inner.animate) {
        qa('.gv-prompt, .gv-prompt-mobile, #gv-mmy, .gv-saved, .gv-status', inner).forEach(function (k) {
          k.animate([{ opacity: 0, offset: 0 }, { opacity: 0, offset: 0.5, easing: FOLD_EASE }, { opacity: 1, offset: 1 }], { duration: UNFOLD_MS, fill: 'forwards' });
        });
      }
      var start = flip(to, from);
      var mv = g.animate([
        { transform: start },
        { transform: 'none' }
      ], { duration: UNFOLD_MS, easing: FOLD_EASE, fill: 'forwards' });
      g.animate([
        { opacity: 0, offset: 0, easing: FOLD_EASE },
        { opacity: 1, offset: 0.3 },
        { opacity: 1, offset: 1 }
      ], { duration: UNFOLD_MS, fill: 'forwards' });
      mv.onfinish = mv.oncancel = function () { if (done) { done(); } if (g.parentNode) { g.parentNode.removeChild(g); } };
      return mv;
    }

    /* v2.17 fix (owner video, 28 Sept: "very choppy on mobile"): the bar
       keeps its slot in the page while folded (it only stops being sticky
       and goes invisible and click-through, rr-brand.css 6d.2), so folding
       never reflows the page. Folding flies a plain dark shape into the car
       icon (transform and opacity only, on a fixed layer). Unfolding at the
       top brings the real bar back in place (opacity and a small scale on
       its container), so it rides with the header as that grows and never
       lands in the wrong spot the way the old flying shape did on iOS. */
    function snapFold(on) {
      barEl.style.height = '';
      if (on) {
        addClass(barEl, 'rr-vfade');
        addClass(barEl, 'rr-vfolded');
        if (vicon) { addClass(vicon, 'on'); }
      } else {
        removeClass(barEl, 'rr-vfolded');
        removeClass(barEl, 'rr-vfade');
        if (vicon) { removeClass(vicon, 'on'); }
      }
    }
    function settle() {
      var want = !!(win.rrCompact && win.rrCompact.on());
      if (want && !folded) { fold(); } else if (!want && folded) { unfold(); }
    }
    function fold() {
      if (!barEl || !isPhone() || folded || flying) { return; }
      folded = true;
      removeClass(barEl, 'gv-expanded');
      var b0 = barEl.getBoundingClientRect();
      var target = foldTarget || iconSlot();
      foldTarget = null;
      if (reducedMotion() || !userScrolled || !barEl.animate || !target || b0.height < 4 || b0.bottom <= 0) {
        snapFold(true);
        return;
      }
      flying = true;
      var f = ghost(b0);
      snapFold(true);                                    /* the copy covers the bar, which hides at once */
      if (vicon) { removeClass(vicon, 'on'); }
      morph(f, b0, target, '50%', function () { flying = false; prepGhost(); settle(); });
      win.setTimeout(function () { if (vicon) { addClass(vicon, 'on'); } }, Math.round(FOLD_MS * 0.55));   /* the icon fades in as the copy lands */
    }
    var unfoldFrom = null;
    function unfold() {
      if (!barEl || !folded || flying) { return; }
      folded = false;
      unfoldRect = null;
      if (sheetOpen) { closeSheet(false); }
      var from = unfoldFrom || iconSlot();
      unfoldFrom = null;
      if (vicon) { removeClass(vicon, 'on'); }          /* the icon fades as the copy leaves it */
      if (reducedMotion() || !barEl.animate || !from) { snapFold(false); return; }
      /* the bar's slot never moved (it kept its place while folded) */
      var to = barEl.getBoundingClientRect();
      if (to.height < 4 || to.bottom <= 0) { snapFold(false); return; }
      flying = true;
      /* the contents' 120ms opacity fade runs now, while the bar is still
         hidden under the copy, so the hand-over at the end shows no blank frame */
      removeClass(barEl, 'rr-vfade');
      var g = ghost(to, true);
      unmorph(g, to, from, function () { snapFold(false); flying = false; prepGhost(); settle(); });
    }
    function measureUnfolded() {
      var h = barEl.style.height;
      var wasFolded = hasClass(barEl, 'rr-vfolded');
      barEl.style.height = '';
      removeClass(barEl, 'rr-vfolded');
      void barEl.offsetHeight;
      var r = barEl.getBoundingClientRect();
      if (wasFolded) { addClass(barEl, 'rr-vfolded'); }
      barEl.style.height = h;
      return { left: r.left, top: r.top, width: r.width, height: r.height };
    }

    /* ---- desktop (29 Sept 2026): the bar scrolls up under the compact row
       with the page and only the chip fades in (6n.3 below). The 6D FLIP fold
       that used to live here was removed in the v7.1 motion review (F10). */
    var dFolded = false;
    /* 29 Sept (owner video 17.50.23: "a lot of white space when the vehicle
       selector bar goes to minimize, just above the featured brand gallery"):
       the faded bar kept its slot in the page, a blank band under the compact
       row. On desktop the bar now stays in the page and scrolls up under the
       compact row like the rest of the page (rr-brand.css 7e: no longer
       sticky), so there is nothing to fold; only the chip fades in. */
    /* 1 Oct 2026 (owner: "mid scroll there's a point where both vehicle search
       bars are present"): while any of the bar still shows below the compact
       row, the chip stays hidden (html.rr-vbar-in, rr-brand.css 6n.3); it
       fades in as the bar passes under the header, and out again on the way
       back. An IntersectionObserver with fine thresholds only reports while
       the bar crosses the top of the screen; one rect read per report. */
    var barIO = null;
    var barIOTop = -1;
    function compactBottom() {
      /* the compact row's resting bottom edge: its fixed top plus its height
         (both unaffected by the slide-in transform) */
      var hdr = q('#header');
      if (!hdr) { return 0; }
      return Math.round((parseFloat(win.getComputedStyle(hdr).top) || 0) + hdr.offsetHeight);
    }
    /* F10: one read on the first fold of the page (the compact state is on
       then), and again only when the announcement bar's height changed
       (annVar reads an inline property, no layout) or after a resize */
    var barAnn = null;
    function watchBarIn() {
      if (!barEl || !('IntersectionObserver' in win)) { return; }
      barAnn = annVar();
      var top = compactBottom();
      if (barIO && top === barIOTop) { return; }
      if (barIO) { barIO.disconnect(); }
      barIOTop = top;
      /* the observed area starts under the compact row, so "intersecting"
         means some of the bar is still showing below the header */
      barIO = new IntersectionObserver(function (entries) {
        var en = entries[entries.length - 1];
        doc.documentElement.classList.toggle('rr-vbar-in', !isPhone() && en.isIntersecting);
      }, { rootMargin: (-top) + 'px 0px 0px 0px', threshold: 0 });
      barIO.observe(barEl);
    }
    function deskFold() {
      if (!barEl || isPhone() || dFolded) { return; }
      dFolded = true;
      removeClass(barEl, 'rr-vdfolded');
      if (!barIO || barAnn !== annVar()) { watchBarIn(); }
    }
    function deskUnfold() {
      if (!barEl || !dFolded) { return; }
      dFolded = false;
      removeClass(barEl, 'rr-vdfolded');
    }

    /* 6n (29 Sept 2026): the compact targets (the chip's lane, the car
       icon) sit in a fixed header, so their rects do not depend on the
       scroll position. They are measured in a quiet moment and reused, so
       the switch itself does not lay the page out three extra times.
       Re-measured after a resize, a new chip label, and on a return to the
       full header only when the announcement bar changed height. */
    var slotCache = null;
    var primeTimer = null;
    var lastScrollAt = 0;
    function primeSlots() {
      slotCache = null;
      win.clearTimeout(primeTimer);
      primeTimer = win.setTimeout(function run() {
        if (!barEl || !win.rrCompact || win.rrCompact.on()) { return; }
        if (Date.now() - lastScrollAt < 250) { primeTimer = win.setTimeout(run, 300); return; }
        var go = function () {
          if (win.rrCompact.on()) { return; }
          if (!isPhone()) { return; }   /* desktop measures nothing ahead (F10) */
          slotCache = { phone: true, ann: annVar(), rect: win.rrCompact.measure(true, iconSlot) };
          prepGhost();
        };
        if (win.requestIdleCallback) { win.requestIdleCallback(go, { timeout: 1500 }); } else { go(); }
      }, 400);
    }
    /* the compact header hangs from the announcement bar; closing the bar
       moves it (read from the inline property, no layout) */
    function annVar() {
      return doc.documentElement.style.getPropertyValue('--rr-announcement-h');
    }
    function cachedSlot() {
      return slotCache && slotCache.phone === isPhone() && slotCache.ann === annVar() ? slotCache.rect : null;
    }
    function onCompactBefore(e) {
      var on = !!(e && e.detail && e.detail.on);
      if (!barEl || !win.rrCompact) { return; }
      if (!isPhone()) { return; }   /* F10: nothing to measure on desktop */
      if (!on && folded) { unfoldFrom = iconSlot(); }
      /* measure the target with the next state applied and transitions off,
         before the class changes for real (the demo's finalRects) */
      if (on && !folded) { foldTarget = cachedSlot() || win.rrCompact.measure(true, iconSlot); }
    }
    function onCompact(e) {
      var on = !!(e && e.detail && e.detail.on);
      syncChip();
      if (!on && isPhone() && !cachedSlot()) { primeSlots(); }
      if (isPhone()) {
        if (on) { fold(); } else { unfold(); }
      } else {
        if (!on) { closeDrop(false); }
        if (folded) { snapFold(false); folded = false; }
        if (on) { deskFold(); } else { deskUnfold(); }
      }
    }

    function watchCompact() {
      buildChip();
      buildVicon();
      syncChip();
      doc.addEventListener('rr:compact-before', function (e) { try { onCompactBefore(e); } catch (err) { /* guarded */ } }, false);
      doc.addEventListener('rr:compact', function (e) { try { onCompact(e); } catch (err) { /* guarded */ } }, false);
      win.addEventListener('scroll', function () { userScrolled = true; }, { passive: true, once: true });
      win.addEventListener('scroll', function () { lastScrollAt = Date.now(); }, { passive: true });
      win.addEventListener('resize', primeSlots, { passive: true });
      win.addEventListener('resize', function () { barAnn = null; }, { passive: true });   /* desktop: re-read on the next fold */
      primeSlots();
      if (win.rrCompact && win.rrCompact.on()) { onCompact({ detail: { on: true } }); }
      doc.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape' && e.keyCode !== 27) { return; }
        if (dropOpen) { closeDrop(true); }
        if (sheetOpen) { closeSheet(true); }
      }, false);
      doc.addEventListener('click', function (e) {
        if (!dropOpen) { return; }
        var t = e.target;
        if (t && (contains(barEl, t) || (chip && contains(chip, t)))) { return; }
        closeDrop(false);
      }, true);
      /* in the sheet the prompt row is its title, not a toggle */
      barEl.addEventListener('click', function (e) {
        if (!sheetOpen) { return; }
        if (e.target && closest(e.target, '.gv-prompt-mobile')) { e.stopPropagation(); e.preventDefault(); }
      }, true);
      /* the chip and icon follow the saved line */
      if (win.MutationObserver) {
        var mo = new win.MutationObserver(function () { try { syncChip(); } catch (err) { /* guarded */ } });
        mo.observe(barEl, { attributes: true, attributeFilter: ['class'] });
        /* D5: any change to the bar's contents drops the prepared fold copy */
        var gmo = new win.MutationObserver(function () { if (ghostReady) { dropGhost(); } });
        gmo.observe(barEl, { childList: true, subtree: true, characterData: true });
      }
      barEl.addEventListener('change', function () { if (ghostReady) { dropGhost(); } }, true);
    }

    /* ---- load trouble (v2.18 6L A2 to A5): see showLoadTrouble. The page
       is never reloaded by itself; the app script is requested again in the
       background and the message sits on its own line under the bar. */
    var loadStart = Date.now();
    var interacted = false;
    var errShown = false;
    var RETRY_KEY = 'rr-gv-retry';
    var BIND_WAIT_MS = 8000;

    function recentRetry() {
      try {
        var v = parseInt(win.sessionStorage.getItem(RETRY_KEY) || '0', 10);
        return v && (Date.now() - v) < 120000;
      } catch (e) { return true; }
    }
    /* v2.18 6L A2, A3, A5: the page is never reloaded by itself. When the
       app has not bound its fields, the app script is requested again in the
       background (twice at most), and the customer gets one line UNDER the
       bar (never over the vehicle line) with a Try again button that does the
       same retry in place; only after that fails does the button offer a
       page reload, and only when pressed. A bar that already shows a vehicle
       (saved or filtered, drawn from the page) keeps quiet: the page works. */
    var gvRetries = 0;
    function gvSrc() {
      var list = doc.getElementsByTagName('script');
      for (var i = 0; i < list.length; i++) {
        if (list[i].src && /car\.govedia\.com\/Scripts\/mmy\.js/.test(list[i].src)) { return list[i].src.replace(/[?&]rrretry=\d+/, ''); }
      }
      return 'https://car.govedia.com/Scripts/mmy.js?v=2.3&shop=raptorracing.myshopify.com';
    }
    function retryApp() {
      gvRetries += 1;
      try { win.__GV_MMY_SCRIPT_LOADED__ = undefined; } catch (e) { /* read-only: the new copy guards itself */ }
      var tag = doc.createElement('script');
      tag.src = gvSrc() + '&rrretry=' + gvRetries;
      tag.async = true;
      (doc.head || doc.documentElement).appendChild(tag);
    }
    function quietState() {
      var st = vehicleState();
      return st === 'saved' || st === 'fit';
    }
    function showLoadTrouble() {
      if (errShown || !barEl || isBound()) { return; }
      if (gvRetries < 1) { retryApp(); }
      if (quietState()) {
        win.setTimeout(function () { try { if (!isBound() && gvRetries < 2) { retryApp(); } } catch (e) { /* guarded */ } }, 8000);
        return;
      }
      errShown = true;
      addClass(barEl, 'rr-gv-trouble');
      var box = doc.createElement('div');
      box.className = 'rr-gv-err';
      box.setAttribute('role', 'status');
      var msg = doc.createElement('p');
      msg.textContent = 'Vehicle search is taking longer than usual.';
      var btn = doc.createElement('button');
      btn.type = 'button';
      btn.className = 'rr-gv-err__retry';
      btn.textContent = 'Try again';
      btn.addEventListener('click', function () {
        if (btn.getAttribute('data-rr-reload')) {
          try { win.sessionStorage.setItem(RETRY_KEY, String(Date.now())); } catch (e) { /* still reload */ }
          win.location.reload();
          return;
        }
        btn.disabled = true;
        btn.textContent = 'Loading';
        retryApp();
        win.setTimeout(function () {
          if (isBound()) { clearLoadTrouble(); return; }
          btn.disabled = false;
          msg.textContent = 'Vehicle search still isn\'t loading.';
          btn.textContent = 'Reload page';
          btn.setAttribute('data-rr-reload', '1');
        }, 6000);
      }, false);
      box.appendChild(msg);
      box.appendChild(btn);
      /* its own line under the bar: a sibling after the dark container, so
         the Make / Model / Year fields keep their full width */
      barEl.appendChild(box);
    }
    function clearLoadTrouble() {
      if (!errShown || !barEl) { return; }
      var box = q('.rr-gv-err', barEl);
      if (box && box.parentNode) { box.parentNode.removeChild(box); }
      removeClass(barEl, 'rr-gv-trouble');
      errShown = false;
    }
    function watchLoadTrouble() {
      var mark = function () { interacted = true; };
      win.addEventListener('scroll', mark, { passive: true, once: true });
      doc.addEventListener('pointerdown', mark, { passive: true, once: true });
      doc.addEventListener('keydown', mark, { once: true });
      if (win.MutationObserver) {
        new win.MutationObserver(function () {
          try {
            if (q('.gv-mmy-load-error', barEl) && !isBound()) { showLoadTrouble(); }
            else if (errShown && isBound()) { clearLoadTrouble(); }
          } catch (e) { /* guarded */ }
        }).observe(barEl, { childList: true, subtree: true });
      }
      win.setTimeout(function () { try { if (!isBound()) { showLoadTrouble(); } } catch (e) { /* guarded */ } }, BIND_WAIT_MS);
    }

    /* ---- 7. mobile --------------------------------------------------------- */
    function syncExpanded() {
      var p = barEl ? q('.gv-prompt-mobile', barEl) : null;
      if (p) { p.setAttribute('aria-expanded', hasClass(barEl, 'gv-expanded') ? 'true' : 'false'); }
    }
    function watchBar() {
      syncExpanded();
      if (!win.MutationObserver || barObserver) { return; }
      barObserver = new win.MutationObserver(function () { try { syncExpanded(); } catch (e) { /* guarded */ } });
      barObserver.observe(barEl, { attributes: true, attributeFilter: ['class'] });
    }

    /* ---- events on the bar ------------------------------------------------- */
    function bindBar() {
      barEl.addEventListener('change', function (e) {
        try {
          var t = e.target;
          if (!t || String(t.tagName).toUpperCase() !== 'SELECT' || !contains(mmyEl, t)) { return; }
          touch();
          refresh();
          updateGarage();
        } catch (err) { /* guarded */ }
      }, false);
      barEl.addEventListener('click', function (e) {
        try {
          var t = e.target;
          if (!t || t.nodeType !== 1) { return; }
          if (closest(t, '.gv-saved__change')) { onChangeVehicle(e); return; }
          if (closest(t, '.gv-saved__garage')) { onGarageClick(e); return; }
          if (closest(t, '.gv-saved__shop')) { onSearchClick(e, closest(t, '.gv-saved__shop')); return; }
          if (closest(t, '.gv-saved__all')) { onShowAll(e); return; }
          if (closest(t, '.gv-saved__clear')) { onClearClick(e); }
        } catch (err) { /* guarded */ }
      }, false);
    }

    /* ---- 1. bind detection: observer and poll ------------------------------ */
    function runAll() {
      bindStep();
      refresh();
      maybeShowSaved();
      watchGarage();
      resumePending();
    }
    function watchMmy() {
      if (!win.MutationObserver || mmyObserver) { return; }
      mmyObserver = new win.MutationObserver(function () { try { runAll(); } catch (e) { /* guarded */ } });
      mmyObserver.observe(mmyEl, { childList: true });
      /* option lists are rebuilt inside the selects after each pick (BindDropdown),
         which the childList observer above does not see; relabel only, cheaply */
      promptObserver = new win.MutationObserver(function () { try { relabel(); } catch (e) { /* guarded */ } });
      promptObserver.observe(mmyEl, { childList: true, subtree: true });
    }
    function stopPoll() {
      if (pollTimer) { win.clearInterval(pollTimer); pollTimer = null; }
    }
    function tick() {
      try { runAll(); } catch (e) { /* guarded */ }
      if ((isBound() && savedSettled) || (Date.now() - pollStart) >= POLL_LIMIT_MS) { stopPoll(); }
    }

    /* ---- 0a. early GoVedia load (28 Sept 2026 review: "Loading results..."
       for more than 6 s). Shopify injects app script tags from its asyncLoad
       list only after window load, so GoVedia's mmy.js started after every
       image and app had finished, and the vehicle results grid waited on
       that. This reads mmy.js's exact URL (version included) from that list
       in the page and loads it as soon as this deferred file runs, right
       after the theme's jQuery. mmy.js guards itself
       (window.__GV_MMY_SCRIPT_LOADED__), so Shopify's later copy skips. */
    function earlyGoVedia() {
      try {
        if (win.__GV_MMY_SCRIPT_LOADED__ || !win.jQuery) { return; }
        var re = /https?:(?:\\?\/){2}car\.govedia\.com(?:\\?\/)Scripts(?:\\?\/)mmy\.js[^"'\s<>]*/;
        var scripts = doc.getElementsByTagName('script');
        var src = '';
        for (var i = 0; i < scripts.length && !src; i++) {
          var s = scripts[i];
          if (s.src && /car\.govedia\.com\/Scripts\/mmy\.js/.test(s.src)) { return; }   /* already requested */
          var text = s.textContent || '';
          if (text.indexOf('govedia') === -1) { continue; }
          var m = re.exec(text);
          if (m) { src = m[0]; }
        }
        if (!src) { return; }
        src = src.replace(/\\\//g, '/').replace(/\\u0026/gi, '&').replace(/&amp;/g, '&');
        var tag = doc.createElement('script');
        tag.src = src;
        tag.async = true;
        (doc.head || doc.documentElement).appendChild(tag);
      } catch (e) { /* Shopify's own late load still happens */ }
    }
    earlyGoVedia();

    /* ---- 0. boot ----------------------------------------------------------- */
    function boot() {
      try {
        mmyEl = q('#gv-mmy');
        barEl = mmyEl ? closest(mmyEl, '.gv-mmy-bar') : null;
        if (!barEl || !mmyEl) { return; }
        earlySaved = barEl.getAttribute('data-rr-early-saved') === 'cookie';
        preRenderFromTag();
        bindBar();
        watchBar();
        watchMmy();
        watchCompact();   /* v2.16: the chip and car icon replace the .rr-vmini compact bar */
        syncResults();    /* v2.17 6J: nudge / count line / Fits chip on collection pages */
        watchActiveRow();
        bindEmpty();
        watchLoadTrouble();
        pollStart = Date.now();
        pollTimer = win.setInterval(tick, POLL_MS);
        tick();
      } catch (e) { /* guarded */ }
    }

    if (doc.readyState === 'loading') {
      doc.addEventListener('DOMContentLoaded', boot, false);
    } else {
      boot();
    }
  } catch (e) {
    /* a GoVedia change must never break the page */
  }
})();
